import { Controller } from "@hotwired/stimulus";
import { inAppShell } from "../lib/app_shell";
import { lockPage, unlockPage } from "../lib/page_lock";

const DOCKED_CLASS = "assistant-docked";
const RESIZING_CLASS = "assistant-resizing";
const WIDTH_PROPERTY = "--assistant-pane-width";
const WIDTH_STORAGE_KEY = "assistantPaneWidth";
const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 320;
// What the page keeps at the pane's widest: the docked menu, the three
// gutters, and enough sheet to still read.
const MIN_PAGE_WIDTH = 320;
const KEY_STEP = 16;
// The phone sheet's slide (assistant-sheet.css).
const SLIDE_MS = 300;

export default class extends Controller<HTMLElement> {
  static targets = ["panel", "workspace", "loading", "error", "closeButton", "library", "resizer", "scrim"];
  static values = { url: String, open: Boolean };

  declare readonly libraryTarget: HTMLElement;
  declare readonly hasResizerTarget: boolean;
  declare readonly resizerTarget: HTMLElement;
  declare readonly hasScrimTarget: boolean;
  declare readonly scrimTarget: HTMLElement;
  private resizing?: { startX: number; startWidth: number };
  private dragging = false;
  private dragStartY = 0;
  private dragDelta = 0;
  private slideTimer?: ReturnType<typeof setTimeout>;
  declare readonly panelTarget: HTMLElement;
  declare readonly workspaceTarget: HTMLElement;
  declare readonly loadingTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly closeButtonTarget: HTMLButtonElement;
  declare urlValue: string;
  declare openValue: boolean;
  private returnFocus?: HTMLElement;
  private linkedSkillPath: string | null = null;
  private readingPosition?: { scroller: HTMLElement; top: number };

  connect(): void {
    this.restorePaneWidth();
    this.render();
    this.openLinkedSkill();
  }

  disconnect(): void {
    this.unwatchKeyboard();
    unlockPage(this.element);
    this.endResize();
    // Turbo temporarily detaches permanent elements while replacing the body.
    // Keep the dock's width until the same panel is reattached.
    if (document.querySelector('meta[name="turbo-permanent-placeholder"][content="global_assistant"]')) return;
    document.documentElement.classList.remove(DOCKED_CLASS);
  }

  // The gutter between the page and the docked pane. Dragging it left widens
  // the pane; the width lives on <html> as the custom property the stylesheet
  // already lays both sheets out from, and is remembered per browser.
  startResize(event: PointerEvent): void {
    if (!this.openValue) return;
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture?.(event.pointerId);
    this.resizing = { startX: event.clientX, startWidth: this.paneWidth() };
    document.documentElement.classList.add(RESIZING_CLASS);
    handle.addEventListener("pointermove", this.resizeMove);
    handle.addEventListener("pointerup", this.resizeEnd);
    handle.addEventListener("pointercancel", this.resizeEnd);
  }

  resizeByKey(event: KeyboardEvent): void {
    const delta = event.key === "ArrowLeft" ? KEY_STEP : event.key === "ArrowRight" ? -KEY_STEP : 0;
    if (!delta) return;
    event.preventDefault();
    this.setPaneWidth(this.paneWidth() + delta);
    this.rememberPaneWidth();
  }

  resetWidth(): void {
    this.setPaneWidth(DEFAULT_WIDTH);
    this.rememberPaneWidth();
  }

  private resizeMove = (event: PointerEvent): void => {
    if (!this.resizing) return;
    this.setPaneWidth(this.resizing.startWidth + (this.resizing.startX - event.clientX));
  };

  private resizeEnd = (): void => {
    this.endResize();
    this.rememberPaneWidth();
  };

  private endResize(): void {
    if (!this.resizing) return;
    this.resizing = undefined;
    document.documentElement.classList.remove(RESIZING_CLASS);
    if (!this.hasResizerTarget) return;
    this.resizerTarget.removeEventListener("pointermove", this.resizeMove);
    this.resizerTarget.removeEventListener("pointerup", this.resizeEnd);
    this.resizerTarget.removeEventListener("pointercancel", this.resizeEnd);
  }

  private paneWidth(): number {
    const width = parseInt(getComputedStyle(document.documentElement).getPropertyValue(WIDTH_PROPERTY), 10);
    return Number.isFinite(width) && width > 0 ? width : DEFAULT_WIDTH;
  }

  private setPaneWidth(width: number): void {
    const menu = document.getElementById("sidebar")?.getBoundingClientRect().width ?? 0;
    const gutters = 3 * 12;
    const max = Math.max(MIN_WIDTH, window.innerWidth - menu - gutters - MIN_PAGE_WIDTH);
    const clamped = Math.round(Math.min(max, Math.max(MIN_WIDTH, width)));
    document.documentElement.style.setProperty(WIDTH_PROPERTY, `${clamped}px`);
  }

  private rememberPaneWidth(): void {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, String(this.paneWidth()));
    } catch {
      // Private mode or blocked storage: the width just does not persist.
    }
  }

  private restorePaneWidth(): void {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(WIDTH_STORAGE_KEY);
    } catch {
      return;
    }
    const width = Number(stored);
    if (Number.isFinite(width) && width >= MIN_WIDTH) this.setPaneWidth(width);
  }

  pageChanged(): void {
    this.render();
    this.restoreReadingPosition();
    this.openLinkedSkill();
  }

  toggle(event?: Event): void {
    if (this.openValue) this.close();
    else this.open(event);
  }

  open(event?: Event): void {
    this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    this.openValue = true;
    this.render();
    this.restoreReadingPosition();
    if (!(event as CustomEvent)?.detail?.navigation && !this.workspaceLoaded) this.retry();
    const skillPath = (event as CustomEvent)?.detail?.skillPath;
    if (skillPath && /^\/assistant_skills\/[1-9]\d*$/.test(skillPath)) {
      this.openSkill();
      this.libraryTarget.setAttribute("src", skillPath);
    }
    else if ((event as CustomEvent)?.detail?.skills) this.showSkills();
    else if ((event as CustomEvent)?.detail?.navigation) this.hideSkills();

    this.closeButtonTarget.focus({ preventScroll: true });
  }

  showSkills(event?: Event): void {
    event?.preventDefault();
    this.rememberReadingPosition();
    this.workspaceTarget.hidden = true;
    this.libraryTarget.hidden = false;
    this.loadingTarget.hidden = true;
    this.errorTarget.hidden = true;
    this.libraryTarget.removeAttribute("src");
    this.libraryTarget.setAttribute("src", this.libraryTarget.dataset.url!);
  }

  openSkill(): void {
    this.rememberReadingPosition();
    this.workspaceTarget.hidden = true;
    this.libraryTarget.hidden = false;
    this.loadingTarget.hidden = true;
    this.errorTarget.hidden = true;
  }

  hideSkills(event?: Event): void {
    this.libraryTarget.hidden = true;
    this.workspaceTarget.hidden = false;
    this.restoreReadingPosition();
    if (event && !this.workspaceLoaded) this.retry();
  }

  // The page renders home into the frame, so an unfetched frame is not an empty one.
  private get workspaceLoaded(): boolean {
    return this.workspaceTarget.hasAttribute("src") || this.workspaceTarget.childElementCount > 0;
  }

  close(): void {
    this.rememberReadingPosition();
    this.openValue = false;
    this.render();
    // The header's launcher is display:none on desktop and the menu's on
    // mobile; the fallback has to be the one the user can see.
    const opener = this.returnFocus?.isConnected
      ? this.returnFocus
      : Array.from(document.querySelectorAll<HTMLElement>("[data-assistant-launcher]")).find(
          (launcher) => launcher.checkVisibility?.() ?? true,
        );
    opener?.focus({ preventScroll: true });
  }

  keydown(event: KeyboardEvent): void {
    if (!this.openValue) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
    }
    if (event.key !== "Tab" || window.matchMedia("(min-width: 768px)").matches) return;
    const controls = Array.from(this.panelTarget.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([type="hidden"]), textarea, select, [contenteditable="true"], [tabindex="0"]',
    )).filter((element) => !element.closest("[hidden]"));
    const destination = event.shiftKey ? controls.at(-1) : controls[0];
    const boundary = event.shiftKey ? controls[0] : controls.at(-1);
    if (document.activeElement === boundary) {
      event.preventDefault();
      destination?.focus();
    }
  }

  loaded(event: Event): void {
    if (event.target !== this.workspaceTarget) return;
    this.loadingTarget.hidden = true;
    this.errorTarget.hidden = true;
  }

  failed(event: Event): void {
    if (event.target !== this.workspaceTarget) return;
    event.preventDefault();
    this.loadingTarget.hidden = true;
    this.errorTarget.hidden = false;
  }

  reset(): void {
    this.readingPosition = undefined;
    this.hideSkills();
    this.libraryTarget.replaceChildren();
    this.workspaceTarget.replaceChildren();
    this.retry();
  }

  rememberReadingPosition(): void {
    const scroller = this.workspaceTarget.querySelector<HTMLElement>('[data-thread-scroll-target~="scroller"]');
    // Turbo detaches permanent elements while replacing the body. Save while
    // the panel still has layout; a hidden or detached scroller reports zero.
    if (scroller?.clientHeight) this.readingPosition = { scroller, top: scroller.scrollTop };
  }

  // A page visit closes the phone sheet: the page is what was asked for, and
  // the conversation is here again on the next open. An overlay (a form sheet,
  // a drawer, a modal) opens above the sheet and leaves it be, so closing that
  // lands back in the same conversation. On desktop the docked pane stays for
  // page visits and steps aside for overlays, which share its edge.
  visit(event?: Event): void {
    const frame = (event?.currentTarget as HTMLElement)?.dataset.turboFrame;
    if (frame === "assistant_skill_library") return;
    const overlay = ["dynamic_modal_frame", "drawer_frame", "sheet_frame"].includes(frame || "");
    if (window.matchMedia("(min-width: 768px)").matches ? overlay : !overlay) this.close();
  }

  // ---- swipe-to-dismiss (phone sheet), as on the sheet frame drawer ----

  dragStart(event: PointerEvent): void {
    if (!this.openValue) return;
    this.dragging = true;
    this.dragStartY = event.clientY;
    this.dragDelta = 0;
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    this.panelTarget.style.transition = "none";
  }

  dragMove(event: PointerEvent): void {
    if (!this.dragging) return;
    // Downward only; the sheet never lifts above its resting place.
    this.dragDelta = Math.max(0, event.clientY - this.dragStartY);
    this.panelTarget.style.transform = `translateY(${this.dragDelta}px)`;
  }

  dragEnd(): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.panelTarget.style.transition = "";
    const threshold = Math.min(this.panelTarget.offsetHeight * 0.3, 140);
    // Clearing the inline transform hands control back to the class: past the
    // threshold, close() slides down from the dragged position; short of it,
    // the sheet eases back up.
    this.panelTarget.style.transform = "";
    if (this.dragDelta > threshold) this.close();
  }

  retry(): void {
    this.loadingTarget.hidden = false;
    this.errorTarget.hidden = true;
    this.workspaceTarget.removeAttribute("src");
    this.workspaceTarget.setAttribute("src", this.urlValue);
  }

  private render(): void {
    const modal = !window.matchMedia("(min-width: 768px)").matches;
    this.slide(modal);
    // Wide enough and the panel docks as a column beside the page instead of
    // floating over it; the stylesheet pads <main> off the class. Set here so
    // resizes (pageChanged) and closes keep it honest. Only the docked shell
    // has the column: the classic layout keeps the floating card.
    document.documentElement.classList.toggle(
      DOCKED_CLASS,
      this.openValue && inAppShell() && window.matchMedia("(min-width: 1280px)").matches,
    );
    // On a phone the sheet is modal: the scrim takes the taps meant for the
    // page and keydown() keeps Tab inside, while a form sheet or drawer a
    // conversation opens still stacks above and works.
    this.panelTarget.setAttribute("role", modal ? "dialog" : "complementary");
    if (modal) this.panelTarget.setAttribute("aria-modal", "true");
    else this.panelTarget.removeAttribute("aria-modal");
    if (this.openValue && modal) this.watchKeyboard();
    else this.unwatchKeyboard();
    document.querySelectorAll('[data-assistant-launcher]').forEach((launcher) => {
      launcher.setAttribute("aria-expanded", String(this.openValue));
    });
  }

  // The phone sheet slides; the card and the column simply show. A closing
  // sheet is hidden once its slide is done, unless it was reopened meanwhile.
  private slide(modal: boolean): void {
    clearTimeout(this.slideTimer);
    const scrim = this.hasScrimTarget ? this.scrimTarget : null;
    // The page behind the sheet must not scroll under a thumb; a card or a
    // column leaves the page alone.
    if (this.openValue && modal) lockPage(this.element);
    else unlockPage(this.element);
    if (!modal) {
      this.panelTarget.hidden = !this.openValue;
      this.panelTarget.classList.remove("is-open");
      if (scrim) { scrim.hidden = true; scrim.classList.remove("is-open"); }
      return;
    }
    if (this.openValue) {
      this.panelTarget.hidden = false;
      if (scrim) scrim.hidden = false;
      this.panelTarget.offsetHeight; // reflow, so the slide starts from off screen
      this.panelTarget.classList.add("is-open");
      scrim?.classList.add("is-open");
      return;
    }
    this.panelTarget.classList.remove("is-open");
    scrim?.classList.remove("is-open");
    this.slideTimer = setTimeout(() => {
      if (this.openValue) return;
      this.panelTarget.hidden = true;
      if (scrim) scrim.hidden = true;
    }, SLIDE_MS);
  }

  // iOS Safari keeps a fixed sheet under its keyboard: the layout viewport does
  // not shrink, only the visual one does. Lifting the sheet's foot by the
  // difference keeps the composer in view while typing.
  private watchKeyboard(): void {
    const viewport = window.visualViewport;
    if (!viewport) return;
    viewport.addEventListener("resize", this.syncKeyboard);
    viewport.addEventListener("scroll", this.syncKeyboard);
    this.syncKeyboard();
  }

  private unwatchKeyboard(): void {
    const viewport = window.visualViewport;
    if (viewport) {
      viewport.removeEventListener("resize", this.syncKeyboard);
      viewport.removeEventListener("scroll", this.syncKeyboard);
    }
    this.panelTarget.style.bottom = "";
  }

  private syncKeyboard = (): void => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const inset = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
    this.panelTarget.style.bottom = inset ? `${inset}px` : "";
  };

  private openLinkedSkill(): void {
    const path = new URL(window.location.href).searchParams.get("assistant_skill");
    if (path === this.linkedSkillPath) return;

    this.linkedSkillPath = path;
    if (!path || !/^\/assistant_skills(?:\/new|\/[1-9]\d*(?:\/edit)?)?$/.test(path)) return;

    this.open(new CustomEvent("assistant:open", { detail: { navigation: true } }));
    this.openSkill();
    this.libraryTarget.setAttribute("src", path);
  }

  private restoreReadingPosition(): void {
    const position = this.readingPosition;
    if (!this.openValue || this.workspaceTarget.hidden || !position) return;

    if (this.workspaceTarget.contains(position.scroller)) position.scroller.scrollTop = position.top;
    this.readingPosition = undefined;
  }

}
